(() => {
  'use strict';

  const CARD_SELECTOR = '[data-ga4-product-card]';
  const observedCards = new WeakSet();
  const exposedCards = new WeakSet();
  const pendingLists = new Map();
  const publishQueue = [];
  let flushScheduled = false;
  let publishRetryCount = 0;
  let publishRetryTimer = null;

  function analyticsPublisher() {
    return window.Shopify?.analytics?.publish;
  }

  function flushPublishQueue() {
    const publisher = analyticsPublisher();
    if (typeof publisher === 'function') {
      while (publishQueue.length) {
        const {name, payload} = publishQueue.shift();
        publisher.call(window.Shopify.analytics, name, payload);
      }
      publishRetryTimer = null;
      return;
    }

    if (publishRetryCount < 100 && publishRetryTimer === null) {
      publishRetryCount += 1;
      publishRetryTimer = window.setTimeout(() => {
        publishRetryTimer = null;
        flushPublishQueue();
      }, 100);
    }
  }

  function publish(name, payload) {
    publishQueue.push({name, payload});
    flushPublishQueue();
  }

  function pageContext() {
    return {
      page_location: window.location.href,
      page_title: document.title,
      page_referrer: document.referrer,
    };
  }

  function variantIdFromLink(link, card) {
    const swatchVariant = link.dataset.swatchVariant;
    if (swatchVariant) return swatchVariant;

    try {
      return new URL(link.href, window.location.origin).searchParams.get('variant') || card.dataset.ga4VariantId;
    } catch (_error) {
      return card.dataset.ga4VariantId;
    }
  }

  function itemIndex(card) {
    const listId = card.dataset.ga4ListId;
    const listName = card.dataset.ga4ListName;
    const cards = Array.from(document.querySelectorAll(CARD_SELECTOR)).filter(
      (candidate) =>
        candidate.dataset.ga4ListId === listId && candidate.dataset.ga4ListName === listName
    );
    const index = cards.indexOf(card);
    return index >= 0 ? index + 1 : Number(card.dataset.ga4Index) || 1;
  }

  function updateCardFromSwatch(card, link) {
    const variantId = link.dataset.swatchVariant;
    if (!variantId) return;

    card.dataset.ga4VariantId = variantId;
    card.dataset.ga4ItemId = `shopify_ZZ_${card.dataset.ga4ProductId}_${variantId}`;
    card.dataset.ga4ItemVariant = link.dataset.ga4ItemVariant || '';
    card.dataset.ga4PriceCents = link.dataset.ga4PriceCents || '0';
  }

  function swatchLinkForVariant(card, variantId) {
    const escapedId = window.CSS && CSS.escape ? CSS.escape(variantId) : variantId;
    return card.querySelector(`.swatch__link[data-swatch-variant="${escapedId}"]`);
  }

  function itemFromCard(card, variantId = card.dataset.ga4VariantId) {
    let itemVariant = card.dataset.ga4ItemVariant;
    let priceCents = card.dataset.ga4PriceCents;

    if (variantId !== card.dataset.ga4VariantId) {
      const swatchLink = swatchLinkForVariant(card, variantId);
      if (swatchLink) {
        itemVariant = swatchLink.dataset.ga4ItemVariant || itemVariant;
        priceCents = swatchLink.dataset.ga4PriceCents || priceCents;
      }
    }

    return {
      item_id: `shopify_ZZ_${card.dataset.ga4ProductId}_${variantId}`,
      item_name: card.dataset.ga4ItemName,
      item_variant: itemVariant,
      item_list_id: card.dataset.ga4ListId,
      item_list_name: card.dataset.ga4ListName,
      index: itemIndex(card),
      price: (Number(priceCents) || 0) / 100,
    };
  }

  function flushListViews() {
    flushScheduled = false;
    pendingLists.forEach((group) => {
      publish('veloura_view_item_list', {
        ...pageContext(),
        currency: group.currency,
        item_list_id: group.id,
        item_list_name: group.name,
        items: group.items,
      });
    });
    pendingLists.clear();
  }

  function queueListView(card) {
    const id = card.dataset.ga4ListId || 'product_list';
    const name = card.dataset.ga4ListName || id;
    const key = `${id}\u0000${name}`;
    const group = pendingLists.get(key) || {
      id,
      name,
      currency: card.dataset.ga4Currency,
      items: [],
    };
    group.items.push(itemFromCard(card));
    pendingLists.set(key, group);

    if (!flushScheduled) {
      flushScheduled = true;
      window.requestAnimationFrame(flushListViews);
    }
  }

  const exposureObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || entry.intersectionRatio < 0.25) return;
              const card = entry.target;
              exposureObserver.unobserve(card);
              if (exposedCards.has(card)) return;
              exposedCards.add(card);
              queueListView(card);
            });
          },
          {threshold: 0.25}
        )
      : null;

  function observeCards(root = document) {
    const cards = [];
    if (root instanceof Element && root.matches(CARD_SELECTOR)) cards.push(root);
    root.querySelectorAll?.(CARD_SELECTOR).forEach((card) => cards.push(card));
    cards.forEach((card) => {
      if (observedCards.has(card)) return;
      observedCards.add(card);
      if (exposureObserver) {
        exposureObserver.observe(card);
      } else {
        exposedCards.add(card);
        queueListView(card);
      }
    });
  }

  function cartItem(item, index) {
    return {
      item_id: `shopify_ZZ_${item.product_id}_${item.variant_id}`,
      item_name: item.product_title,
      item_variant: item.variant_title || undefined,
      price: (Number(item.final_price) || 0) / 100,
      quantity: Number(item.quantity) || 0,
      index: index + 1,
    };
  }

  async function publishCartView(uiSurface) {
    try {
      const response = await fetch(`${window.theme.routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: {Accept: 'application/json'},
      });
      if (!response.ok) return;
      const cart = await response.json();
      publish('veloura_view_cart', {
        ...pageContext(),
        ui_surface: uiSurface,
        currency: cart.currency,
        value: (Number(cart.total_price) || 0) / 100,
        items: cart.items.map(cartItem),
      });
    } catch (_error) {
      // Cart analytics must never interrupt the cart experience.
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest(`${CARD_SELECTOR} a[href*="/products/"]`);
    if (!link) return;
    const card = link.closest(CARD_SELECTOR);
    if (link.classList.contains('swatch__link')) {
      updateCardFromSwatch(card, link);
      return;
    }
    const variantId = variantIdFromLink(link, card);
    publish('veloura_select_item', {
      ...pageContext(),
      currency: card.dataset.ga4Currency,
      item_list_id: card.dataset.ga4ListId,
      item_list_name: card.dataset.ga4ListName,
      items: [itemFromCard(card, variantId)],
    });
  });

  document.addEventListener('theme:cart:open', () => publishCartView('cart_drawer'));

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) observeCards(node);
      });
    });
  });

  observeCards();
  mutationObserver.observe(document.documentElement, {childList: true, subtree: true});

  if (document.body.classList.contains('template-cart')) {
    publishCartView('cart_page');
  }
})();
